import { Icon16GiftOutline } from '@vkontakte/icons';
import React, { type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { BillingDataPartner, BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { isFamilySubsInTariff } from 'reactApp/appHelpers/featuresHelpers';
import { autoRenewalBannerEnabled, unbindCardBannerEnabled } from 'reactApp/appHelpers/featuresHelpers/features/autoRenewalCommunication';
import { isDiscountOutflowOldProduct } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { MultiSessionDeviceSubs } from 'reactApp/appHelpers/featuresHelpers/features/multiSessionSubscriptions';
import { features } from 'reactApp/constants/products/features';
import type { IFeatureData } from 'reactApp/constants/products/features.types';
import { PARTNER_TITLE } from 'reactApp/modules/billingData/billing.data';
import {
    BillingDataPromoSubscriptionType,
    isImapSubscription,
    isTBankSubscription,
    isVkMusicReward,
} from 'reactApp/modules/billingData/billingData.types';
import { BuySubscriptionSelectors } from 'reactApp/modules/buySubscription/buySubscription.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import { AUTOUPLOAD_10TB_REGEX, FLEXIBLE_QUOTA_TARIFFS_REGEX } from 'reactApp/modules/products/products.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import {
    isFamilyPlaceByMemberSubscription,
    isGraceSubscription,
    isVkMusicSubscription,
} from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { PersonalDiscountPrice } from 'reactApp/sections/SubscriptionsPage/ui/PersonalDiscountPrice/PersonalDiscountPrice';
import { AutoRenewalWarning } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/AutoRenewalWarning/AutoRenewalWarning';
import { Card } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/Card/Card';
import { UnbindCardWarning } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/UnbindCardWarning/UnbindCardWarning';
import { SubscriptionItemLoader } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionLoader/SubscriptionItemLoader';
import { SubscriptionMobileName } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.types';
import { FeaturesList } from 'reactApp/ui/FeaturesList/FeaturesList';
import { FreemiumFeaturesList } from 'reactApp/ui/FreemiumFeaturesList/FreemiumFeaturesList';
import { GraceSubscription } from 'reactApp/ui/GraceSubscription/GraceSubscription';
import { MultiSessionFeaturesList } from 'reactApp/ui/MultiSessionFeaturesList/MultiSessionFeaturesList';
import { formatDefaultDate, formatFileHistoryDate } from 'reactApp/utils/formatDate';
import {
    getPeriodName,
    getPeriodNameAsWord,
    getTrialOrDiscountDateEnd,
    getTypeOfPeriodName,
    isMonth3Period,
    isMonth6Period,
    isYear2Period,
    nowIsTrialOrDiscountPeriod,
} from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';

import styles from '../SubscriptionModal.css';

enum EInfoTypes {
    conditions = 'conditions',
    endDate = 'endDate',
    nextDate = 'nextDate',
    options = 'options',
    disko = 'disko',
    card = 'card',
    freemiumConditions = 'freemiumConditions',
    freemiumOptions = 'freemiumOptions',
    freemiumEndDate = 'freemiumEndDate',
    multiSessionFeatures = 'multiSessionFeatures',
    autoRenewalWarning = 'autoRenewalWarning',
    unbindCardWarning = 'unbindCardWarning',
}

enum EInfoTitles {
    conditions = 'Тариф',
    endDate = 'Дата окончания подписки',
    nextDate = 'Следующий платёж',
    options = 'Возможности подписки',
    optionsV2 = 'Опции подписки',
    disko = 'Лицензия на Диск-О:',
    card = 'Способ оплаты',
    freemiumConditions = 'Условия подписки',
    freemiumEndDate = 'Окончание подписки',
}

interface IInfo {
    id: EInfoTypes;
    title?: EInfoTitles;
    content: ReactElement;
    footer?: ReactElement;
}

type TSubscriptionsInfos = IInfo[];

type UseInfoParams = {
    subscription: any;
    product: any;
    partner: any;
    sendAnalytics: any;
    paused: boolean;
    onClose?: () => void;
};

export const useInfo = ({ subscription, product, partner, sendAnalytics, paused, onClose }: UseInfoParams): TSubscriptionsInfos => {
    const { card } = useSelector(CardSelectors.getCard);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const canSetDiscountOutflow = useSelector(BuySubscriptionSelectors.getCanSetDiscountInOutflow);
    const discountOutflowPercent = useSelector(BuySubscriptionSelectors.getDiscountInOutflowPercent);
    const discountOutflowPrice = useSelector(BuySubscriptionSelectors.getDiscountInOutflow);
    const isDiscountInOutflowLoading = useSelector(BuySubscriptionSelectors.isDiscountInOutflowLoading);
    const vkMusicSubscription = isVkMusicSubscription(subscription);
    const vkMusicReward = isVkMusicReward(subscription);
    const isFamilyPlaceByMember = isFamilyPlaceByMemberSubscription(subscription);
    const isFreemium = subscription?.isFreemium;

    const infos: TSubscriptionsInfos = [];

    let conditions = '';

    const isTrialPeriod = product?.trialPeriod && nowIsTrialOrDiscountPeriod(product?.trialPeriod, subscription.start);

    const purchaseCost = subscription.purchaseCost;

    const renewalCost = subscription.renewalCost;

    let productPeriodSuffix =
        isYear2Period(product?.period) || isMonth6Period(product?.period)
            ? '— разовый платёж'
            : `в ${getTypeOfPeriodName(product?.period)}`;

    if (isMonth3Period(product?.period)) {
        productPeriodSuffix = 'на 3 месяца';
    }

    let bonusLabel;

    if (
        subscription?.type === BillingDataPlatform.ios ||
        subscription?.type === BillingDataPlatform.android ||
        subscription?.type === BillingDataPlatform.rustore
    ) {
        conditions = `Мобильная подписка ${SubscriptionMobileName[subscription.type]}`;
    } else if ((subscription?.isTrial ?? isTrialPeriod) && product?.trialPeriod) {
        const trialDateEnd = getTrialOrDiscountDateEnd(product?.trialPeriod, subscription.start);

        const freeCondition = `${getPeriodNameAsWord(product?.trialPeriod, false, true)} бесплатно`;

        const discountCost = subscription.isTrial ? renewalCost : product?.discountPrice;

        const discountCondition =
            product?.discountPeriod && `затем ${formatPrice(discountCost, 'ru')} ₽ в ${getTypeOfPeriodName(product?.discountPeriod)}`;

        const defaultCondition = `${formatPrice(renewalCost, 'ru')} ₽ в ${getTypeOfPeriodName(product?.period)}`;
        // subscriptions/list и billing/data не возвращают эту информацию
        const upsaleTrialAferDateCondition = `${formatPrice(product?.price, 'ru')} ₽ в ${getTypeOfPeriodName(product?.period)}`;

        conditions = `${freeCondition}, затем ${defaultCondition}`;

        if (discountCondition) {
            const discountDateEnd = getTrialOrDiscountDateEnd(product?.discountPeriod, trialDateEnd);
            conditions = `${freeCondition}, ${discountCondition}, с ${formatFileHistoryDate(
                discountDateEnd.valueOf()
            )} — ${upsaleTrialAferDateCondition}`;
        }
    } else if (subscription?.type === BillingDataPromoSubscriptionType.quota && !partner) {
        conditions = isFamilyPlaceByMember ? 'Семейный' : isTBankSubscription(subscription) ? 'Подписка Т-Банк' : 'Подарок';
    } else if (vkMusicReward) {
        conditions = subscription?.space?.value || '';
    } else if (partner) {
        conditions = `Подписка ${PARTNER_TITLE[partner]}`;
    } else if (!subscription?.base) {
        const periodLetter = product?.period.slice(-1);
        let period = product?.discountPeriodCount ? `${product?.discountPeriodCount}${periodLetter}` : product?.discountPeriod;
        const isDiscountPeriod = product?.discountPeriod && nowIsTrialOrDiscountPeriod(period, subscription.start);
        // для billing/data discountCost может быть равна цене продукта, однако это ок, потому что ниже есть условие на период
        const discountCost = purchaseCost || product?.discountPrice;

        /**
         * tempexp_17360-next-line
         * @TODO: CLOUDWEB-17360: Нестандартный скидочный период - 13 месяцев.
         * По дизайну надо показыва инфо про 1 год + лейбл про подарок в 1 месяц.
         * Сделать нормальное решение после АБ.
         */
        if (period === '13m') {
            period = '12m';
            bonusLabel = '30 дней';
        }

        if (period === '15m') {
            period = '12m';
            bonusLabel = '3 месяца';
        }
        const preposition = product?.discountPeriodCount ? 'по' : 'за';

        conditions =
            product?.discountPeriod && discountCost && (subscription.isDiscount ?? isDiscountPeriod)
                ? `${getPeriodNameAsWord(period, false, true)} ${preposition} ${formatPrice(discountCost, 'ru')} ₽, затем ${formatPrice(
                      /** для составных периодов скидки, мы в renewalCost получаем цену следующего СКИДОЧНОГО продления,
                       *  а не ЦЕНОЙ после скидок*/
                      product?.discountPeriodCount ? product?.price : renewalCost,
                      'ru'
                  )} ₽ в ${getTypeOfPeriodName(product?.period)}`
                : `${formatPrice(renewalCost, 'ru')} ₽ ${productPeriodSuffix}`;
    }

    let conditionsContent = isDiscountInOutflowLoading ? (
        <SubscriptionItemLoader />
    ) : (
        <>
            {conditions}
            {/* tempexp_17360-next-line */}
            {bonusLabel && (
                <span>
                    <Icon16GiftOutline />
                    {bonusLabel}
                </span>
            )}
        </>
    );

    const isOldFlowDiscountAbProduct = isDiscountOutflowOldProduct(product?.id);
    // поддержка старого флоу экспа скидка в оттоке
    if (isOldFlowDiscountAbProduct) {
        conditionsContent = <PersonalDiscountPrice newPrice={product?.price} period={product?.period} />;
    }
    // проверяем, есть информация о скидке и что не можем установить новую скидку
    if (!canSetDiscountOutflow && discountOutflowPercent && discountOutflowPrice) {
        conditionsContent = <PersonalDiscountPrice oldPrice={product?.price} newPrice={discountOutflowPrice} period={product?.period} />;
    }

    if (isFreemium) {
        infos.push({
            id: EInfoTypes.freemiumConditions,
            title: EInfoTitles.freemiumConditions,
            content: <>Бесплатная пробная подписка на {getPeriodName(product?.trialPeriod || '7d', false, true, true)}</>,
        });
    } else if (conditions) {
        infos.push({
            id: EInfoTypes.conditions,
            title: EInfoTitles.conditions,
            content: conditionsContent,
        });
    }

    let footer;
    if (isGraceSubscription(subscription)) {
        footer = <GraceSubscription subscription={subscription} product={product} />;
    }

    if (paused && subscription?.renewAt) {
        infos.push({
            id: EInfoTypes.nextDate,
            title: EInfoTitles.nextDate,
            content: <>{`${formatFileHistoryDate(subscription.renewAt)}, возобновится после паузы`}</>,
            footer,
        });
    } else if (subscription?.autorenewal && subscription?.renewAt) {
        infos.push({
            id: EInfoTypes.nextDate,
            title: EInfoTitles.nextDate,
            content: <>{formatFileHistoryDate(subscription.renewAt)}</>,
            footer,
        });
    } else if (vkMusicReward) {
        infos.push({
            id: EInfoTypes.nextDate,
            title: EInfoTitles.nextDate,
            content: <>{formatFileHistoryDate(subscription.expires)}</>,
            footer,
        });
    } else if (isFreemium && subscription?.expires) {
        infos.push({
            id: EInfoTypes.freemiumEndDate,
            title: EInfoTitles.freemiumEndDate,
            content: <>{formatDefaultDate(subscription.expires)}</>,
            footer,
        });
    } else if (subscription?.expires) {
        infos.push({
            id: EInfoTypes.endDate,
            title: EInfoTitles.endDate,
            content: <>{formatFileHistoryDate(subscription.expires)}</>,
            footer,
        });
    }

    if (autoRenewalBannerEnabled && subscription?.autorenewal === false && !subscription?.isFreemium && !isCorpUser && !isBizUser) {
        infos.push({
            id: EInfoTypes.autoRenewalWarning,
            content: <AutoRenewalWarning />,
        });
    } else if (
        unbindCardBannerEnabled &&
        !card &&
        subscription?.type === BillingDataPlatform.web &&
        !subscription?.isFreemium &&
        !isCorpUser &&
        !isBizUser
    ) {
        infos.push({
            id: EInfoTypes.unbindCardWarning,
            content: <UnbindCardWarning subscription={subscription} onClose={onClose} />,
        });
    }

    const isSferum = partner === BillingDataPartner.sferum;
    const isVkCombo = partner === BillingDataPartner.mail;
    const partnerUpload = partner && (isSferum || isVkCombo);
    let featuresOverride: IFeatureData[] | undefined;

    // tempexp_16481-next-line
    const autoupload10TB = AUTOUPLOAD_10TB_REGEX.test(product?.id);

    // tempexp_16598-start
    const isCheapest = product?.id && Object.values(cheapestProductIDs).includes(product?.id);
    if (isCheapest) {
        featuresOverride = features.cheapest;
    }
    // tempexp_16598-end

    // tempexp_18683-next-line
    const isFlexSpace = product?.id && FLEXIBLE_QUOTA_TARIFFS_REGEX.test(product.id);

    if (vkMusicSubscription) {
        const spaceValue = subscription?.space?.value;
        featuresOverride = features.vkMusic;
        if (spaceValue) {
            const [vkMusicFeature, ...restFeatures] = features.vkMusic;
            const spaceText = `+${spaceValue} в едином пространстве для файлов`;
            featuresOverride = [
                vkMusicFeature,
                {
                    text: spaceText,
                    key: 'space',
                },
                ...restFeatures,
            ];
        }
    }

    if (isTBankSubscription(subscription)) {
        const quotaText = {
            text: `${subscription.space.space} гигабайт в Облаке и Почте`,
            key: 'quota',
            primaryColor: true,
        };
        const updateFeatures = [...features.TBankFeatures];
        updateFeatures.splice(1, 0, quotaText);
        featuresOverride = updateFeatures;
    }

    /* tempexp_20439-start */
    const hideFeatures =
        (product?.id && MultiSessionDeviceSubs.enabled && MultiSessionDeviceSubs.isFreeChoiceProduct(product.id)) ||
        isImapSubscription(subscription);
    const isPresetProduct = product?.id && MultiSessionDeviceSubs.enabled && MultiSessionDeviceSubs.isPresetInSubsProduct(product.id);
    const isPresetProductWithAds = isPresetProduct && MultiSessionDeviceSubs.isPresetInSubsProductWithAds(product.id);
    /* tempexp_20439-end */

    if (isFreemium) {
        infos.push({
            id: EInfoTypes.freemiumOptions,
            content: <FreemiumFeaturesList />,
        });
        /* tempexp_20439-start */
    } else if (isPresetProduct) {
        const deviceCount = MultiSessionDeviceSubs.getDeviceCount(product.id);
        infos.push({
            id: EInfoTypes.multiSessionFeatures,
            title: EInfoTitles.optionsV2,
            content: <MultiSessionFeaturesList deviceCount={deviceCount} productWithAds={isPresetProductWithAds} />,
        });
        /* tempexp_20439-end */
    } else if (!hideFeatures) {
        infos.push({
            id: EInfoTypes.options,
            title: EInfoTitles.options,
            content: (
                <FeaturesList
                    partnerUpload={partnerUpload}
                    base={subscription?.base}
                    upload={subscription?.flags?.upload && !subscription?.isDisko && !subscription?.isProfessional && !subscription?.isMail}
                    paidFeatures={
                        !isSferum &&
                        subscription?.flags?.paidFeatures &&
                        !subscription?.isDisko &&
                        !subscription?.isProfessional &&
                        !subscription?.isMail
                    }
                    autodelete={
                        !isSferum &&
                        subscription?.flags?.paidFeatures &&
                        !subscription?.isDisko &&
                        !subscription?.isProfessional &&
                        !subscription?.isMail
                    }
                    disko={subscription?.isDisko && !subscription?.isProfessional}
                    modalPro={subscription?.isProfessional && !subscription?.isMail}
                    isPromoPro={Boolean(subscription?.isProfessional && subscription?.promoId && !subscription?.isMail)}
                    isSingleQuota={subscription?.isMail && !subscription?.isProfessional}
                    isSingleQuotaPro={subscription?.isMail && subscription?.isProfessional}
                    isSingleQuotaFamily={!partner && !subscription?.isProfessional && subscription?.isMail && isFamilySubsInTariff}
                    isCorpUser={isCorpUser}
                    space={subscription?.space}
                    plusSpace
                    itemClassName={styles.condition}
                    partner={partner}
                    // tempexp_16481-next-line
                    autoupload10TB={autoupload10TB}
                    listOverride={featuresOverride}
                    isFlexSpace={isFlexSpace}
                    showAds={!subscription?.disableAds}
                />
            ),
        });
    }

    if (subscription?.diskoKey) {
        infos.push({
            id: EInfoTypes.disko,
            title: EInfoTitles.disko,
            content: <>{subscription.diskoKey}</>,
        });
    }

    if (card && subscription?.type === BillingDataPlatform.web && subscription?.autorenewal) {
        infos.push({
            id: EInfoTypes.card,
            title: EInfoTitles.card,
            content: <Card sendAnalytics={sendAnalytics} />,
        });
    }

    return infos;
};
